import { useEffect, useState } from 'react';
import { parseUrlParams, type WebViewParams } from '@core/config/url-params';
import { setAuthToken } from '@core/http/client';
import { useNavigationStore } from './navigation-store';
import { MissingParamsError } from './missing-params-error';
import { AppRouter } from './routes';
import { ProductAPI } from '@base/api/product-api';

export function UrlParamsValidator() {
  const setUrlParams = useNavigationStore((state) => state.setUrlParams);
  const setResolvedItemCode = useNavigationStore((state) => state.setResolvedItemCode);
  const setIsResolvingItemCode = useNavigationStore((state) => state.setIsResolvingItemCode);

  const [validation, setValidation] = useState<{
    isValid: 'pending' | 'waiting_token' | 'resolving' | 'success' | 'error';
    params?: WebViewParams;
    errors?: string[];
  }>(() => {
    try {
      const params = parseUrlParams();
      // If token is already in URL (dev/testing), proceed immediately
      if (params.token) {
        return { isValid: 'pending' as const, params };
      }
      // No token in URL — wait for postMessage from Flutter
      return { isValid: 'waiting_token' as const, params };
    } catch (error) {
      if (error instanceof Error) {
        try {
          const errorMessages = JSON.parse(error.message);
          return {
            isValid: 'error' as const,
            errors: errorMessages.map((e: { message: string }) => e.message),
          };
        } catch {
          return { isValid: 'error' as const, errors: [error.message] };
        }
      }
      return { isValid: 'error' as const, errors: ['Invalid URL params'] };
    }
  });

  // Listen for auth token via postMessage (secure JS bridge from Flutter)
  useEffect(() => {
    if (validation.isValid !== 'waiting_token') return;

    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'auth' && event.data?.token) {
        console.log('[UrlParamsValidator] 🔐 Received token via postMessage');
        setAuthToken(event.data.token);
        // Update params with received token and proceed
        const updatedParams = { ...validation.params!, token: event.data.token };
        setValidation({ isValid: 'pending', params: updatedParams });
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [validation]);

  useEffect(() => {
    if (validation.isValid === 'pending' && validation.params) {
      const params = validation.params;
      // Set token if available (from URL or postMessage)
      if (params.token) {
        setAuthToken(params.token);
      }
      setUrlParams(params);

      // If item_code is missing but item_id exists, resolve it via GraphQL
      if (!params.item_code && params.item_id) {
        setIsResolvingItemCode(true);
        setValidation({ isValid: 'resolving', params });

        const productAPI = new ProductAPI();
        productAPI
          .resolveProductCode(params.item_id!)
          .then((resolvedCode) => {
            if (resolvedCode) {
              setResolvedItemCode(resolvedCode);
            }
            setIsResolvingItemCode(false);
            setValidation({ isValid: 'success', params });
          })
          .catch((error) => {
            console.error('[UrlParamsValidator] Failed to resolve item_code:', error);
            setIsResolvingItemCode(false);
            setValidation({ isValid: 'success', params });
          });
      } else {
        // item_code is present, no resolution needed
        if (params.item_code) {
          setResolvedItemCode(params.item_code);
        }
        setValidation({ isValid: 'success', params });
      }
    }
  }, [setUrlParams, validation]);

  if (validation.isValid === 'error') {
    return <MissingParamsError errors={validation.errors!} />;
  }

  if (validation.isValid === 'waiting_token') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <p>Đang xác thực...</p>
      </div>
    );
  }

  if (validation.isValid === 'resolving') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <p>Đang tải thông tin sản phẩm...</p>
      </div>
    );
  }

  if (validation.isValid !== 'success') {
    return null;
  }

  return <AppRouter />;
}
